import { Helmet } from "react-helmet-async";
import { Phone } from "lucide-react";
import customerCareIcon from "@/assets/customer-care-icon.png";
import proActionHeroBg from "@/assets/pro-action-hero-bg.jpg";
import proActionLogo from "@/assets/pro-action-logo.png";

const ServiceProAction = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Rapports Pro Action - Agroref</title>
        <meta name="description" content="Rapports Pro Action pour surveiller et améliorer la qualité de votre lait" />
        <link rel="preload" as="image" href={proActionHeroBg} />
      </Helmet>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 min-h-[600px] flex items-center overflow-hidden">
          <img src={proActionHeroBg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="sync" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl bg-black/75 backdrop-blur-md rounded-lg p-8 md:p-12">
              <div className="w-16 h-1 bg-red-600 mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Rapports Pro Action: Votre Clé pour un Lait de Qualité Supérieure
              </h1>
              <p className="text-lg text-white/80 mb-6">
                La qualité du lait est un facteur crucial dans la production laitière. Le rapport Pro Action, rapport de laboratoire, offre un aperçu précis de cette qualité. Il mesure des éléments tels que les cellules somatiques, les germes, parmi d&apos;autres indicateurs de qualité.
              </p>
              <p className="text-lg text-white/80 mb-8">
                Les rapports Pro Action sont essentiels pour les producteurs laitiers. Ils aident à surveiller la qualité du lait et à identifier les problèmes de santé des vaches. Chez Agro, nous fournissons des tests de laboratoire pour des rapports Pro Action précis. Nous offrons aussi des conseils pour améliorer la qualité du lait.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Contactez-nous dès maintenant!
              </a>
            </div>
          </div>
        </section>

        {/* Section: Importance des rapports */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">L&apos;importance des Rapports Pro Action</h2>
              
              <div className="grid md:grid-cols-2 gap-8 text-muted-foreground">
                <div className="space-y-6">
                  <p>
                    Les rapports Pro Action sont une ressource inestimable pour tout producteur de lait qui se soucie de la qualité de sa production. Ces rapports fournissent une <strong>analyse détaillée de la composition du lait</strong>, permettant de surveiller des indicateurs clés tels que le niveau de cellules somatiques et le nombre de germes.
                  </p>
                  
                  <p>
                    Plus qu&apos;un simple indicateur de la qualité actuelle du lait, les rapports Pro Action peuvent révéler d&apos;éventuels <strong>problèmes de santé</strong> au sein du troupeau qui pourraient affecter négativement la qualité du lait produit. L&apos;identification précoce de ces problèmes peut permettre d&apos;intervenir rapidement pour résoudre la situation.
                  </p>
                </div>

                <div className="space-y-6">
                  <p>
                    En outre, ces rapports permettent aux éleveurs de personnaliser précisément leur approche de l&apos;alimentation et de la gestion du troupeau. Cela signifie qu&apos;ils peuvent agir de manière <strong>proactive</strong> pour maintenir et améliorer la qualité du lait produit, prévenant ainsi les défis majeurs avant qu&apos;ils ne surviennent.
                  </p>
                  
                  <p>
                    Chez Agro, nous comprenons l&apos;importance vitale de ces rapports pour la gestion efficace d&apos;une exploitation laitière. C&apos;est pourquoi nous nous dédions à offrir des <strong>services de laboratoire de haute qualité</strong> pour produire des rapports Pro Action précis et pertinents, vous aidant à améliorer constamment la qualité de votre lait.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Outil indispensable */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Rapports Pro Action: Outil Indispensable pour la Qualité de Votre Lait</h2>
              
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    Chez Agro, nous avons élaboré un processus détaillé pour créer nos rapports Pro Action. Cette procédure commence par la <strong>collecte de données</strong> sur les paramètres du lait et la gestion du troupeau. Grâce à ces informations, nous pouvons obtenir une image complète de votre exploitation laitière, nous permettant de fournir les analyses les plus précises possible.
                  </p>
                  
                  <p>
                    Une fois que nous avons collecté ces données, nous effectuons une <strong>analyse en laboratoire détaillée</strong>. Nous mesurons les niveaux de cellules somatiques et de bactéries dans le lait, ainsi que d&apos;autres indicateurs clés de qualité. Cette approche nous permet de déterminer exactement quels aspects de votre lait pourraient nécessiter des améliorations.
                  </p>
                  
                  <p>
                    Enfin, nous utilisons ces données pour créer des <strong>rapports Pro Action détaillés et personnalisés</strong> pour chaque client. Ces rapports mettent en évidence les zones potentielles d&apos;amélioration et proposent des recommandations concrètes pour augmenter la qualité du lait. Grâce à notre service, vous pourrez identifier rapidement les problèmes de qualité, prendre des mesures préventives, et augmenter votre productivité et votre rentabilité.
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <img 
                    src={proActionLogo}
                    alt="Pro Action"
                    className="w-full max-w-md h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 bg-[#333333] overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-xl font-semibold mb-6 text-white/80">Nous sommes là pour vous!</p>
            <h3 className="text-2xl font-bold mb-4 text-white">Améliorez Votre Production Laitière dès Aujourd&apos;hui</h3>
            <p className="text-lg text-white/70 mb-8 max-w-3xl mx-auto">
              N&apos;attendez plus pour augmenter la qualité de votre lait grâce aux rapports Pro Action d&apos;Agro. En exploitant notre expertise personnalisée et nos technologies de pointe, nous vous offrons des insights précieux pour une amélioration notable de votre production. Découvrez comment nos services spécialisés peuvent transformer positivement votre exploitation laitière, contribuer à une meilleure santé de votre troupeau et maximiser votre productivité.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <Phone className="h-5 w-5" />
              Contactez-nous dès maintenant!
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceProAction;
